/**
 * Silent auto-update: compares the build id baked into this bundle against the
 * one the server currently serves (/version.json). On mismatch it clears any
 * service-worker caches and hard-reloads, so a client that kept a tab open for
 * days still lands on the latest deploy without ever being asked.
 *
 * Deliberately quiet: no UI, no toast, no "a new version is available" prompt.
 */

import { BUILD_ID } from "../version";

const RELOAD_GUARD_KEY = "ck_version_reloaded_for";

/** Local dev / prerender must never self-reload (puppeteer would hang). */
function isLocal() {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

/**
 * Fetches the deployed build id. Returns null on any failure (offline, blocked,
 * missing file) — a failed check must never break the app.
 */
async function fetchDeployedBuildId() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.buildId === "string" ? data.buildId : null;
  } catch (error) {
    // Offline or blocked — silent, the next check will retry.
    return null;
  }
}

async function clearCaches() {
  try {
    if (!("caches" in window)) return;
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch (error) {
    console.error("[CK-6920] Failed clearing caches before version reload", {
      errorCode: "CK-6920",
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Runs one version check.
 *
 * @param {Object} options
 * @param {boolean} options.skipReload - when true (e.g. unsaved form open),
 *   detect the mismatch but leave the page alone; the next check reloads.
 * @returns {Promise<boolean>} true if a reload was triggered.
 */
export async function checkAppVersion({ skipReload = false } = {}) {
  if (isLocal()) return false;

  const deployedBuildId = await fetchDeployedBuildId();
  if (!deployedBuildId || deployedBuildId === BUILD_ID) return false;
  if (skipReload) return false;

  // Loop guard: if the server ever serves a version.json that disagrees with
  // the bundle it also serves (stale CDN edge, half-finished deploy), reload
  // at most once per deployed id instead of spinning forever.
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === deployedBuildId) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, deployedBuildId);
  } catch (error) {
    // Private mode / storage disabled — proceed without the guard.
  }

  await clearCaches();
  window.location.reload();
  return true;
}
